import { useEffect, useMemo } from "react";
import { Link, useLocation } from "wouter";
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  HelpCircle,
  Pin,
  Search,
  Settings2,
  Sun,
  Moon,
  Monitor,
  X,
} from "lucide-react";
import { useSidebar } from "@/components/ui/sidebar";
import { useCommandPalette } from "@/components/vc";
import { useTheme } from "@/components/theme-provider";
import { useBranding, resolveLogoSrc } from "@/components/branding-provider";
import { useHelpDrawer } from "@/components/vc/VCHelpDrawer";
import { useMe } from "@/lib/auth";
import { usePreference } from "@/hooks/use-preference";
import { getIcon } from "@/lib/sidebar-icons";
import {
  getDashboardProfitLossItem,
  groupMobileSidebarItems,
  type MobileSidebarGroup,
  type MobileSidebarGroupKey,
} from "@/lib/sidebar-groups";
import type { SidebarItemNode } from "@/lib/sidebar-config";
import { UserMenu } from "./user-menu";
import { cn } from "@/lib/utils";
import { METALUI_BRAND } from "@workspace/brand";

export type MobileBadgeCounts = {
  unreadMessages: number;
  pendingSocialApprovals: number;
  unreadSocialInbox: number;
  pendingPacking: number;
  openReturns: number;
  overdueTasks: number;
};

type MobileNavigationProps = {
  navItems: SidebarItemNode[];
  recentPages: Array<{ href: string; label: string }>;
  favouriteIds: string[];
  badgeCounts: MobileBadgeCounts;
  fallbackIconColor: string | null;
  onUnpin: (id: string) => void;
  onPin: (id: string) => void;
  isReady: boolean;
  hasError: boolean;
};

function isActive(location: string, href: string) {
  return href === "/" ? location === "/" : location === href || location.startsWith(`${href}/`);
}

function readSelectedTenantId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem("vc.selectedTenantId");
  } catch {
    return null;
  }
}

function resolveBadge(item: SidebarItemNode, counts: MobileBadgeCounts) {
  switch (item.badgeKey) {
    case "unreadMessages": return counts.unreadMessages;
    case "pendingSocialApprovals": return counts.pendingSocialApprovals;
    case "unreadSocialInbox": return counts.unreadSocialInbox;
    case "pendingPacking": return counts.pendingPacking;
    case "openReturns": return counts.openReturns;
    case "overdueTasks": return counts.overdueTasks;
    default: return 0;
  }
}

function findActiveGroup(groups: MobileSidebarGroup[], location: string): MobileSidebarGroupKey | null {
  for (const group of groups) {
    if (group.items.some((item) => isActive(location, item.href) || item.children.some((child) => isActive(location, child.href)))) {
      return group.key;
    }
  }
  return null;
}

function MobileRouteIcon({ item, active }: { item: SidebarItemNode; active: boolean }) {
  const Icon = getIcon(item.icon) || Settings2;
  return <Icon className={cn("h-[17px] w-[17px] shrink-0", active && "text-[var(--sidebar-gold)]")} aria-hidden="true" />;
}

function RouteLink({
  item,
  location,
  badgeCounts,
  fallbackIconColor,
  onPin,
  onUnpin,
  isPinned,
  onNavigate,
}: {
  item: SidebarItemNode;
  location: string;
  badgeCounts: MobileBadgeCounts;
  fallbackIconColor: string | null;
  onPin: (id: string) => void;
  onUnpin: (id: string) => void;
  isPinned: boolean;
  onNavigate: () => void;
}) {
  const active = isActive(location, item.href);
  const badge = resolveBadge(item, badgeCounts);
  const external = item.openInNewWindow || /^https?:\/\//.test(item.href);
  const content = (
    <>
      <MobileRouteIcon item={item} active={active} />
      <span className={cn("min-w-0 flex-1 truncate", active && "font-semibold")}>{item.name}</span>
      {badge > 0 && (
        <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-[var(--sidebar-gold)] px-1.5 text-[10px] font-bold text-black" aria-label={`${badge} notifications`}>
          {badge > 99 ? "99+" : badge}
        </span>
      )}
      {external && <ExternalLink className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/45" aria-label="Opens in a new window" />}
    </>
  );
  const linkClass = cn(
    "flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-[10px] px-3 text-left text-[13px] outline-none transition-colors",
    "hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring active:bg-sidebar-accent",
    active && "bg-[var(--sidebar-selected)] text-sidebar-foreground before:absolute before:left-0 before:h-6 before:w-[3px] before:rounded-r before:bg-[var(--sidebar-gold)]",
  );

  return (
    <div className="group relative flex min-w-0 items-center gap-1">
      {external ? (
        <a className={linkClass} href={item.href} target="_blank" rel="noopener noreferrer" aria-current={active ? "page" : undefined} onClick={onNavigate}>
          {content}
        </a>
      ) : (
        <Link className={linkClass} href={item.href} aria-current={active ? "page" : undefined} onClick={onNavigate} style={fallbackIconColor ? { color: active ? undefined : fallbackIconColor } : undefined}>
          {content}
        </Link>
      )}
      <button
        type="button"
        className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] text-sidebar-foreground/45 outline-none hover:bg-sidebar-accent hover:text-[var(--sidebar-gold)] focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        aria-label={isPinned ? `Unpin ${item.name}` : `Pin ${item.name}`}
        aria-pressed={isPinned}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          isPinned ? onUnpin(item.id) : onPin(item.id);
        }}
      >
        <Pin className={cn("h-3.5 w-3.5", isPinned && "fill-current text-[var(--sidebar-gold)]")} aria-hidden="true" />
      </button>
    </div>
  );
}

function MobileGroup({
  group,
  open,
  location,
  badgeCounts,
  fallbackIconColor,
  favouriteIds,
  onPin,
  onUnpin,
  onNavigate,
  onToggle,
}: {
  group: MobileSidebarGroup;
  open: boolean;
  location: string;
  badgeCounts: MobileBadgeCounts;
  fallbackIconColor: string | null;
  favouriteIds: string[];
  onPin: (id: string) => void;
  onUnpin: (id: string) => void;
  onNavigate: () => void;
  onToggle: () => void;
}) {
  const contentId = `mobile-nav-group-${group.key}`;
  return (
    <section className="border-t border-sidebar-border/80 first:border-t-0" data-testid={`mobile-nav-group-${group.key}`}>
      <button
        type="button"
        className="flex min-h-12 w-full items-center gap-3 px-3 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-sidebar-foreground/60 outline-none hover:text-sidebar-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={onToggle}
      >
        <span className="flex-1">{group.label}</span>
        {open ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
      </button>
      {open && (
        <div id={contentId} className="space-y-0.5 pb-2" data-testid={`mobile-nav-group-content-${group.key}`}>
          {group.items.map((item) => {
            const itemActive = isActive(location, item.href);
            const hasChildren = item.children.length > 0;
            return (
              <div key={item.id} className="px-1">
                <RouteLink
                  item={item}
                  location={location}
                  badgeCounts={badgeCounts}
                  fallbackIconColor={fallbackIconColor}
                  isPinned={favouriteIds.includes(item.id)}
                  onPin={onPin}
                  onUnpin={onUnpin}
                  onNavigate={onNavigate}
                />
                {hasChildren && (open || itemActive) && (
                  <div className="ml-6 border-l border-sidebar-border/70 pl-1" data-testid={`mobile-nav-children-${item.id}`}>
                    {item.children.map((child) => (
                      <RouteLink
                        key={child.id}
                        item={child}
                        location={location}
                        badgeCounts={badgeCounts}
                        fallbackIconColor={fallbackIconColor}
                        isPinned={favouriteIds.includes(child.id)}
                        onPin={onPin}
                        onUnpin={onUnpin}
                        onNavigate={onNavigate}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function MobileNavigation({
  navItems,
  recentPages,
  favouriteIds,
  badgeCounts,
  fallbackIconColor,
  onUnpin,
  onPin,
  isReady,
  hasError,
}: MobileNavigationProps) {
  const [location] = useLocation();
  const { isMobile, isDrawer, openMobile, setOpenMobile } = useSidebar();
  const { openPalette } = useCommandPalette();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { openHelp } = useHelpDrawer();
  const { me } = useMe();
  const branding = useBranding();
  const tenantId = readSelectedTenantId() ?? me?.tenants?.[0]?.id ?? null;
  const currentTenant = me?.tenants?.find((tenant) => tenant.id === tenantId) ?? me?.tenants?.[0];
  const groups = useMemo(() => groupMobileSidebarItems(navItems), [navItems]);
  const dashboard = navItems.find((item) => item.href === "/");
  const dashboardProfitLoss = getDashboardProfitLossItem(navItems);
  const activeGroup = findActiveGroup(groups, location);
  const [openGroups, setOpenGroups] = usePreference<string[]>(`mobile-nav-open-groups:${tenantId ?? "default"}`, []);

  useEffect(() => {
    const valid = new Set(groups.map((group) => group.key));
    setOpenGroups((previous) => {
      const next = previous.filter((key): key is MobileSidebarGroupKey =>
        valid.has(key as MobileSidebarGroupKey),
      );
      return next.length === previous.length &&
        next.every((key, index) => key === previous[index])
        ? previous
        : next;
    });
  }, [groups, setOpenGroups]);

  useEffect(() => {
    if (!activeGroup) return;
    setOpenGroups((previous) => {
      if (previous.includes(activeGroup)) return previous;
      return isMobile ? [activeGroup] : [...previous, activeGroup].slice(-2);
    });
  }, [activeGroup, isMobile, setOpenGroups]);

  useEffect(() => {
    if (!isDrawer) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [isDrawer]);

  const close = () => setOpenMobile(false);
  const openSearch = () => {
    if (isDrawer && openMobile) {
      setOpenMobile(false);
      window.setTimeout(openPalette, 250);
      return;
    }
    openPalette();
  };
  const toggleGroup = (key: MobileSidebarGroupKey) => {
    setOpenGroups((previous) => {
      if (previous.includes(key)) return previous.filter((value) => value !== key);
      return isMobile ? [key] : [...previous.filter((value) => value !== key), key].slice(-2);
    });
  };
  const flatItems = navItems.flatMap((item) => [item, ...item.children]);
  const recent = recentPages
    .map((page) => flatItems.find((item) => item.href === page.href))
    .filter((item): item is SidebarItemNode => item !== undefined && item.id !== dashboardProfitLoss?.id)
    .slice(0, 5);
  const pinned = favouriteIds
    .map((id) => flatItems.find((item) => item.id === id))
    .filter((item): item is SidebarItemNode => item !== undefined && item.id !== dashboardProfitLoss?.id)
    .slice(0, 5);
  const quickAccess = [...pinned, ...recent.filter((recentItem) => !pinned.some((item) => item.id === recentItem.id))].slice(0, 6);

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-sidebar text-sidebar-foreground min-[1025px]:hidden" data-mobile-navigation data-testid="mobile-navigation">
      <header className="shrink-0 border-b border-sidebar-border bg-sidebar px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <div className="flex min-h-11 items-center gap-3">
          <img
            src={resolveLogoSrc(branding.logoUrl) || `${import.meta.env.BASE_URL}${resolvedTheme === "dark" ? METALUI_BRAND.shieldFile : METALUI_BRAND.shieldFileBlack}`}
            alt=""
            className="h-8 w-8 shrink-0 object-contain"
          />
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate font-serif text-[15px] font-semibold text-sidebar-primary">{branding.appName}</p>
            {currentTenant && <p className="truncate text-[10px] uppercase tracking-[0.16em] text-sidebar-foreground/50">{currentTenant.name}</p>}
          </div>
          <button type="button" className="flex h-11 w-11 items-center justify-center rounded-[10px] outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring" onClick={close} aria-label="Close navigation">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <button
          type="button"
          onClick={openSearch}
          className="mt-3 flex h-11 w-full items-center gap-3 rounded-full border border-sidebar-border bg-sidebar-accent/50 px-4 text-left text-sm text-sidebar-foreground/55 outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          aria-label="Search MetalUI"
          data-testid="mobile-nav-search"
        >
          <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1">Search MetalUI...</span>
          <kbd className="hidden rounded border border-sidebar-border px-1.5 py-0.5 text-[10px] min-[430px]:inline">⌘K</kbd>
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-2" data-testid="mobile-nav-body">
        <div className="space-y-1 pb-3">
          {dashboard && (
            <RouteLink item={dashboard} location={location} badgeCounts={badgeCounts} fallbackIconColor={fallbackIconColor} isPinned={favouriteIds.includes(dashboard.id)} onPin={onPin} onUnpin={onUnpin} onNavigate={close} />
          )}
          {dashboardProfitLoss && (
            <RouteLink item={dashboardProfitLoss} location={location} badgeCounts={badgeCounts} fallbackIconColor={fallbackIconColor} isPinned={favouriteIds.includes(dashboardProfitLoss.id)} onPin={onPin} onUnpin={onUnpin} onNavigate={close} />
          )}
          {quickAccess.length > 0 && (
            <section className="border-t border-sidebar-border/80 pt-2" aria-label="Quick access">
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-sidebar-foreground/45">Recent & pinned</p>
              {quickAccess.map((item) => (
                <RouteLink key={`quick-${item.id}`} item={item} location={location} badgeCounts={badgeCounts} fallbackIconColor={fallbackIconColor} isPinned={favouriteIds.includes(item.id)} onPin={onPin} onUnpin={onUnpin} onNavigate={close} />
              ))}
            </section>
          )}
        </div>

        {!isReady ? (
          <p className="px-3 py-4 text-xs text-sidebar-foreground/55">Loading navigation…</p>
        ) : (
          <>
            {hasError && (
              <p className="px-3 py-3 text-xs text-destructive" role="status">
                Navigation is using the last available menu.
              </p>
            )}
            {groups.length === 0 ? (
              <p className="px-3 py-4 text-xs text-sidebar-foreground/55">No navigation items available for your role.</p>
            ) : (
              <div>
                {groups.map((group) => (
                  <MobileGroup
                    key={group.key}
                    group={group}
                    open={openGroups.includes(group.key)}
                    location={location}
                    badgeCounts={badgeCounts}
                    fallbackIconColor={fallbackIconColor}
                    favouriteIds={favouriteIds}
                    onPin={onPin}
                    onUnpin={onUnpin}
                    onNavigate={close}
                    onToggle={() => toggleGroup(group.key)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <footer className="shrink-0 border-t border-sidebar-border bg-sidebar px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2">
        <div className="flex items-center gap-1">
          <span className="px-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-sidebar-foreground/45">Appearance</span>
          <div className="ml-auto flex rounded-lg border border-sidebar-border p-0.5" role="group" aria-label="Appearance">
            {([
              ["light", Sun, "Light"],
              ["dark", Moon, "Dark"],
              ["system", Monitor, "System"],
            ] as const).map(([value, Icon, label]) => (
              <button key={value} type="button" onClick={() => setTheme(value)} className={cn("flex h-9 w-9 items-center justify-center rounded-md text-sidebar-foreground/55 outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring", theme === value && "bg-[var(--sidebar-selected)] text-[var(--sidebar-gold)]")} aria-label={`${label} theme`} aria-pressed={theme === value}>
                <Icon className="h-4 w-4" aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
        <div className="mt-1 flex items-center gap-1">
          <button type="button" onClick={() => openHelp("general")} className="flex h-11 flex-1 items-center gap-2 rounded-lg px-2 text-sm text-sidebar-foreground/70 outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring" data-testid="mobile-nav-help">
            <HelpCircle className="h-4 w-4" aria-hidden="true" /> Help
          </button>
          <div className="min-w-0 flex-[2]">
            <UserMenu />
          </div>
        </div>
      </footer>
    </div>
  );
}